/**
 * chartUtils.ts - Shared utilities for Smart Chart modules
 *
 * Extracted from SmartChartWorkspace.tsx and SmartChartResult.tsx
 * to eliminate code duplication (K4, S2, S3).
 */

import React from 'react';
import type { ChartStyleOverrides, ReferenceLine } from '../components/ChartStyleEditor';
import { COLOR_THEMES } from '../components/ChartStyleEditor';
import type { AIExplanation } from '../types';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DotChartOutlined,
  HeatMapOutlined,
  BoxPlotOutlined,
  FundOutlined,
  RadarChartOutlined,
  ControlOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

// ---- S3: Unified color constants ----
export const COLORS = {
  primary: '#533afd',
  primaryEnd: '#6b54fd',
  success: '#15be53',
  warning: '#e6a200',
  danger: '#df1b41',
  text: '#333',
  textSecondary: '#666',
  border: '#e8e8e8',
};

// ---- Chart icon map ----
export const CHART_ICONS: Record<string, React.ReactNode> = {
  bar: React.createElement(BarChartOutlined),
  line: React.createElement(LineChartOutlined),
  pie: React.createElement(PieChartOutlined),
  scatter: React.createElement(DotChartOutlined),
  boxplot: React.createElement(BoxPlotOutlined),
  histogram: React.createElement(BarChartOutlined),
  heatmap: React.createElement(HeatMapOutlined),
  radar: React.createElement(RadarChartOutlined),
  area: React.createElement(FundOutlined),
  pareto: React.createElement(BarChartOutlined),
  bubble: React.createElement(DotChartOutlined),
  funnel: React.createElement(BarChartOutlined),
  treemap: React.createElement(AppstoreOutlined),
  candlestick: React.createElement(FundOutlined),
  i_mr: React.createElement(ControlOutlined),
  xbar_r: React.createElement(ControlOutlined),
  xbar_s: React.createElement(ControlOutlined),
  p_chart: React.createElement(ControlOutlined),
  np_chart: React.createElement(ControlOutlined),
  c_chart: React.createElement(ControlOutlined),
  u_chart: React.createElement(ControlOutlined),
  ewma: React.createElement(ControlOutlined),
  cusum: React.createElement(ControlOutlined),
  capability_hist: React.createElement(ExperimentOutlined),
  qq_plot: React.createElement(DotChartOutlined),
  correlation_matrix: React.createElement(HeatMapOutlined),
  main_effects: React.createElement(FundOutlined),
  interaction_plot: React.createElement(FundOutlined),
  regression_scatter: React.createElement(DotChartOutlined),
  feature_importance: React.createElement(BarChartOutlined),
  residual_plot: React.createElement(DotChartOutlined),
  effects_pareto: React.createElement(BarChartOutlined),
  response_surface: React.createElement(HeatMapOutlined),
  variance_components: React.createElement(BarChartOutlined),
  weibull_plot: React.createElement(DotChartOutlined),
  reliability_curve: React.createElement(LineChartOutlined),
  pca_scree: React.createElement(BarChartOutlined),
  pca_biplot: React.createElement(DotChartOutlined),
  cluster_scatter: React.createElement(DotChartOutlined),
  distribution_fit: React.createElement(BarChartOutlined),
  forecast_plot: React.createElement(LineChartOutlined),
  seasonal_decomp: React.createElement(LineChartOutlined),
};

// ---- Chart labels ----
export const CHART_LABELS: Record<string, string> = {
  bar: '柱状图', line: '折线图', pie: '饼图', scatter: '散点图',
  boxplot: '箱线图', histogram: '直方图', heatmap: '热力图',
  radar: '雷达图', area: '面积图', pareto: '排列图',
  bubble: '气泡图', funnel: '漏斗图', treemap: '矩形树图',
  candlestick: 'K线图',
  i_mr: 'I-MR 控制图', xbar_r: 'Xbar-R 控制图', xbar_s: 'Xbar-S 控制图',
  p_chart: 'P 控制图', np_chart: 'NP 控制图', c_chart: 'C 控制图',
  u_chart: 'U 控制图', ewma: 'EWMA 控制图', cusum: 'CUSUM 控制图',
  capability_hist: '能力直方图', qq_plot: 'QQ 正态概率图',
  correlation_matrix: '相关矩阵图', main_effects: '主效应图',
  interaction_plot: '交互作用图',
  regression_scatter: '回归散点图', feature_importance: '特征重要性图',
  residual_plot: '残差分析图', effects_pareto: '效应帕累托图',
  response_surface: '3D 响应曲面', variance_components: '方差分量图',
  weibull_plot: 'Weibull 概率图', reliability_curve: '可靠度函数图',
  pca_scree: 'PCA 碎石图', pca_biplot: 'PCA 双标图',
  cluster_scatter: '聚类散点图', distribution_fit: '分布拟合图',
  forecast_plot: '趋势预测图', seasonal_decomp: '季节分解图',
};

// ---- DataZoom type sets ----
export const DATAZOOM_SLIDER_TYPES = new Set(['line', 'area', 'bar', 'histogram', 'pareto', 'candlestick']);
export const DATAZOOM_SCROLL_TYPES = new Set(['scatter', 'bubble']);

// ---- MECE categories (S2) ----
export const CATEGORIES = [
  {
    key: 'overview',
    label: '数据概览',
    desc: '了解数据分布、构成与基本面貌',
    color: COLORS.primary,
    types: new Set(['histogram', 'boxplot', 'qq_plot', 'distribution_fit', 'pie', 'pareto', 'radar', 'funnel', 'treemap']),
  },
  {
    key: 'trend',
    label: '趋势与预测',
    desc: '随时间或序列的变化趋势',
    color: '#4facfe',
    types: new Set(['line', 'area', 'forecast_plot', 'seasonal_decomp', 'candlestick']),
  },
  {
    key: 'relation',
    label: '关系与建模',
    desc: '变量间的关联、因果与回归',
    color: '#43e97b',
    types: new Set(['scatter', 'regression_scatter', 'bubble', 'correlation_matrix', 'heatmap', 'feature_importance', 'residual_plot', 'response_surface']),
  },
  {
    key: 'compare',
    label: '比较与实验',
    desc: '组间差异对比与实验因子分析',
    color: '#f093fb',
    types: new Set(['bar', 'main_effects', 'interaction_plot', 'effects_pareto', 'variance_components']),
  },
  {
    key: 'spc',
    label: '过程控制',
    desc: 'SPC 过程监控与能力评估',
    color: COLORS.primaryEnd,
    types: new Set(['i_mr', 'xbar_r', 'xbar_s', 'p_chart', 'np_chart', 'c_chart', 'u_chart', 'ewma', 'cusum', 'capability_hist']),
  },
  {
    key: 'explore',
    label: '探索与可靠性',
    desc: '降维聚类与寿命可靠性分析',
    color: '#fa709a',
    types: new Set(['pca_scree', 'pca_biplot', 'cluster_scatter', 'weibull_plot', 'reliability_curve']),
  },
];

// ---- Chart action button style ----
export const chartActionBtnStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.85)',
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  color: COLORS.primary,
  width: 28, height: 28,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ---- Apply style overrides to ECharts option ----
export function applyStyleOverrides(
  baseOption: Record<string, any>,
  overrides: ChartStyleOverrides,
  chartType: string,
): Record<string, any> {
  const option = JSON.parse(JSON.stringify(baseOption));

  // Color theme
  if (overrides.colorTheme && COLOR_THEMES[overrides.colorTheme]) {
    option.color = COLOR_THEMES[overrides.colorTheme].colors;
  }

  // Title via ECharts title component
  if (overrides.title !== undefined && overrides.title !== '') {
    option.title = {
      ...(option.title || {}),
      text: overrides.title,
      textStyle: {
        fontSize: overrides.titleFontSize || 18,
        fontWeight: 600,
        color: '#061b31',
      },
      left: 'center',
    };
  }

  // Label font size
  if (overrides.labelFontSize) {
    if (option.xAxis) {
      const xa = Array.isArray(option.xAxis) ? option.xAxis : [option.xAxis];
      xa.forEach((ax: any) => {
        ax.axisLabel = { ...(ax.axisLabel || {}), fontSize: overrides.labelFontSize };
      });
      option.xAxis = xa.length === 1 ? xa[0] : xa;
    }
    if (option.yAxis) {
      const ya = Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis];
      ya.forEach((ax: any) => {
        ax.axisLabel = { ...(ax.axisLabel || {}), fontSize: overrides.labelFontSize };
      });
      option.yAxis = ya.length === 1 ? ya[0] : ya;
    }
  }

  // Axis names
  if (overrides.xAxisName !== undefined && option.xAxis) {
    const xa = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
    xa.name = overrides.xAxisName;
    if (Array.isArray(option.xAxis)) option.xAxis[0] = xa;
    else option.xAxis = xa;
  }
  if (overrides.yAxisName !== undefined && option.yAxis) {
    const ya = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
    ya.name = overrides.yAxisName;
    if (Array.isArray(option.yAxis)) option.yAxis[0] = ya;
    else option.yAxis = ya;
  }

  // Y axis min/max
  if (option.yAxis) {
    const ya = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
    if (overrides.yAxisMin !== undefined) ya.min = overrides.yAxisMin;
    if (overrides.yAxisMax !== undefined) ya.max = overrides.yAxisMax;
    if (Array.isArray(option.yAxis)) option.yAxis[0] = ya;
    else option.yAxis = ya;
  }

  // Legend position
  if (overrides.legendPosition && option.legend) {
    const legend = Array.isArray(option.legend) ? option.legend[0] : option.legend;
    delete legend.top; delete legend.bottom; delete legend.left; delete legend.right; delete legend.orient;
    switch (overrides.legendPosition) {
      case 'top': legend.top = 0; legend.left = 'center'; break;
      case 'bottom': legend.bottom = 0; legend.left = 'center'; break;
      case 'left': legend.left = 0; legend.top = 'middle'; legend.orient = 'vertical'; break;
      case 'right': legend.right = 0; legend.top = 'middle'; legend.orient = 'vertical'; break;
    }
    if (Array.isArray(option.legend)) option.legend[0] = legend;
    else option.legend = legend;
  }

  // Reference lines injection
  if (overrides.referenceLines && overrides.referenceLines.length > 0) {
    const series = option.series || [];
    for (const line of overrides.referenceLines) {
      if (line.direction === 'horizontal') {
        if (series.length > 0) {
          if (!series[0].markLine) series[0].markLine = { silent: true, symbol: 'none', data: [] };
          series[0].markLine.data.push({
            yAxis: line.value,
            lineStyle: { type: line.lineStyle, color: line.color, width: 2 },
            label: { formatter: line.label, position: 'insideEndTop', color: line.color, fontSize: 11, fontWeight: 'bold' },
          });
        }
      } else {
        if (series.length > 0) {
          if (!series[0].markLine) series[0].markLine = { silent: true, symbol: 'none', data: [] };
          series[0].markLine.data.push({
            xAxis: line.value,
            lineStyle: { type: line.lineStyle, color: line.color, width: 2 },
            label: { formatter: line.label, position: 'insideEndTop', color: line.color, fontSize: 11, fontWeight: 'bold' },
          });
        }
      }
    }
    option.series = series;
  }

  // Ensure grid.right has enough margin to prevent text overflow
  if (option.grid) {
    const g = option.grid;
    const parseRight = (v: any) => typeof v === 'string' ? parseInt(v, 10) : (typeof v === 'number' ? v : 0);
    if (parseRight(g.right) < 8) g.right = '8%';
    option.grid = g;
  }

  // Default legend position: if user hasn't set it and legend is at top (blocking title), move to bottom
  if (!overrides.legendPosition && option.legend) {
    const legends = Array.isArray(option.legend) ? option.legend : [option.legend];
    legends.forEach((leg: any) => {
      if (!leg.bottom && !leg.right && !leg.left) {
        leg.bottom = 0;
        leg.left = 'center';
        delete leg.top;
      }
    });
    option.legend = legends.length === 1 ? legends[0] : legends;
  }

  return option;
}

// ---- Inject smart Y-axis range for all value axes ----
// Prevents the "data at 500, axis starts at 0" problem by computing data-driven min/max.
// Skips: category axes, axes that already have explicit min/max, frequency/count axes (histogram).
export function injectAxisRange(option: Record<string, any>): Record<string, any> {
  if (!option.series || !Array.isArray(option.series)) return option;
  // Skip 3D charts
  if (option.grid3D || option.xAxis3D || option.yAxis3D) return option;

  const FREQ_NAMES = new Set(['频次', '频数', '密度', '频率', 'ACF', 'PACF', '%Study Var', 'count']);
  const BOUNDED_NAMES = new Set(['可靠度 R(t)', '生存概率 S(t)', 'F(t)', '真阳性率 (TPR)', 'R²']);

  function shouldSkipAxis(ax: any): boolean {
    if (!ax || ax.type === 'category' || ax.type === 'log') return true;
    if (ax.min !== undefined && ax.max !== undefined) return true;
    const name = (ax.name || '').trim();
    if (FREQ_NAMES.has(name)) return true;
    if (BOUNDED_NAMES.has(name)) return true;
    return false;
  }

  // Extract all numeric values from series data for a given axis index
  function extractValues(series: any[], axisKey: 'xAxis' | 'yAxis', axisIndex: number): number[] {
    const vals: number[] = [];
    for (const s of series) {
      const sIdx = (axisKey === 'yAxis' ? s.yAxisIndex : s.xAxisIndex) ?? 0;
      if (sIdx !== axisIndex) continue;
      const data = s.data;
      if (!Array.isArray(data)) continue;
      const isBoxplot = s.type === 'boxplot';
      for (const d of data) {
        if (typeof d === 'number') {
          vals.push(d);
        } else if (Array.isArray(d)) {
          if (isBoxplot || d.length >= 5) {
            // Boxplot tuple: [min, Q1, median, Q3, max] — push all values
            for (const n of d) { if (typeof n === 'number' && isFinite(n)) vals.push(n); }
          } else {
            // [x, y] format
            const v = axisKey === 'yAxis' ? d[1] : d[0];
            if (typeof v === 'number' && isFinite(v)) vals.push(v);
          }
        } else if (d && typeof d === 'object') {
          const raw = d.value;
          if (Array.isArray(raw)) {
            if (isBoxplot || raw.length >= 5) {
              for (const n of raw) { if (typeof n === 'number' && isFinite(n)) vals.push(n); }
            } else {
              const v = axisKey === 'yAxis' ? raw[1] : raw[0];
              if (typeof v === 'number' && isFinite(v)) vals.push(v);
            }
          } else if (typeof raw === 'number' && isFinite(raw)) {
            vals.push(raw);
          }
        }
      }
      // Also check markLine data for control limits
      if (s.markLine?.data) {
        for (const ml of s.markLine.data) {
          if (axisKey === 'yAxis' && ml.yAxis !== undefined) vals.push(ml.yAxis);
          if (axisKey === 'xAxis' && ml.xAxis !== undefined) vals.push(ml.xAxis);
        }
      }
    }
    return vals;
  }

  function computeRange(vals: number[]): { min: number; max: number } | null {
    if (vals.length === 0) return null;
    const dMin = Math.min(...vals);
    const dMax = Math.max(...vals);
    const range = dMax - dMin;
    if (range === 0) return { min: dMin - 1, max: dMax + 1 };
    const pad = range * 0.08;
    // Round to a "nice" step for clean axis labels
    const rawMin = dMin - pad;
    const rawMax = dMax + pad;
    const step = Math.pow(10, Math.floor(Math.log10(range))) / 2; // e.g. range=9 → step=5; range=0.7 → step=0.05
    return {
      min: Math.floor(rawMin / step) * step,
      max: Math.ceil(rawMax / step) * step,
    };
  }

  function processAxes(axisKey: 'xAxis' | 'yAxis') {
    const raw = option[axisKey];
    if (!raw) return;
    const axes = Array.isArray(raw) ? raw : [raw];
    let changed = false;
    for (let i = 0; i < axes.length; i++) {
      const ax = axes[i];
      if (shouldSkipAxis(ax)) continue;
      // If only one of min/max is set, skip (intentional partial constraint like min:0 for MR charts)
      if (ax.min !== undefined || ax.max !== undefined) continue;
      const vals = extractValues(option.series, axisKey, i);
      const r = computeRange(vals);
      if (r) {
        ax.min = r.min;
        ax.max = r.max;
        changed = true;
      }
    }
    if (changed) {
      option[axisKey] = axes.length === 1 ? axes[0] : axes;
    }
  }

  processAxes('yAxis');
  processAxes('xAxis');
  return option;
}

// ---- Inject toolbox (saveAsImage + dataZoom + restore) ----
export function injectToolbox(option: Record<string, any>): Record<string, any> {
  if (option.toolbox) return option;
  return {
    ...option,
    toolbox: {
      show: true, right: 8, top: 4,
      iconStyle: { borderColor: '#999' },
      feature: {
        saveAsImage: { pixelRatio: 2, title: '保存图片' },
        dataZoom: { title: { zoom: '框选缩放', back: '还原' } },
        restore: { title: '还原' },
      },
    },
  };
}

// ---- Inject dark mode styling into chart option ----
export function injectDarkMode(option: Record<string, any>, isDark: boolean): Record<string, any> {
  if (!isDark) return option;
  option.backgroundColor = 'transparent';
  option.textStyle = { color: '#ccc', ...(option.textStyle || {}) };
  const darkAxis = {
    axisLabel: { color: '#aaa' },
    axisLine: { lineStyle: { color: '#444' } },
    splitLine: { lineStyle: { color: '#333' } },
    nameTextStyle: { color: '#aaa' },
  };
  const applyAxis = (axes: any) => {
    const arr = Array.isArray(axes) ? axes : [axes];
    arr.forEach((ax: any) => {
      if (!ax) return;
      ax.axisLabel = { ...darkAxis.axisLabel, ...(ax.axisLabel || {}) };
      ax.axisLine = { lineStyle: { ...darkAxis.axisLine.lineStyle, ...(ax.axisLine?.lineStyle || {}) } };
      ax.splitLine = { lineStyle: { ...darkAxis.splitLine.lineStyle, ...(ax.splitLine?.lineStyle || {}) } };
      ax.nameTextStyle = { ...darkAxis.nameTextStyle, ...(ax.nameTextStyle || {}) };
    });
  };
  if (option.xAxis) applyAxis(option.xAxis);
  if (option.yAxis) applyAxis(option.yAxis);
  if (option.legend) {
    const legs = Array.isArray(option.legend) ? option.legend : [option.legend];
    legs.forEach((l: any) => { l.textStyle = { color: '#aaa', ...(l.textStyle || {}) }; });
  }
  if (option.toolbox) {
    option.toolbox.iconStyle = { borderColor: '#666', ...(option.toolbox.iconStyle || {}) };
  }
  // graphic text color
  if (option.graphic && Array.isArray(option.graphic)) {
    option.graphic.forEach((g: any) => {
      if (g.style?.fill && g.style.fill === '#061b31') g.style.fill = '#a0b4ff';
    });
  }
  return option;
}

// ---- Inject dataZoom ----
export function injectDataZoom(option: Record<string, any>, chartType: string): Record<string, any> {
  if (DATAZOOM_SLIDER_TYPES.has(chartType)) {
    if (!option.dataZoom) {
      option.dataZoom = [
        {
          type: 'slider', show: true, start: 0, end: 100, bottom: 8, height: 24,
          borderColor: '#ddd', fillerColor: `rgba(102, 126, 234, 0.15)`,
          handleStyle: { color: COLORS.primary },
        },
        { type: 'inside', start: 0, end: 100 },
      ];
      option.grid = { ...(option.grid || {}), bottom: Math.max((option.grid?.bottom as number) || 0, 60) };
    }
  } else if (DATAZOOM_SCROLL_TYPES.has(chartType)) {
    if (!option.dataZoom) {
      option.dataZoom = [
        { type: 'inside', xAxisIndex: 0 },
        { type: 'inside', yAxisIndex: 0 },
      ];
    }
  }
  return option;
}

// ---- Generate HTML report ----
export function generateHtmlReport(
  title: string,
  chartOption: Record<string, any>,
  explanation: AIExplanation,
  fileIdOrParams: string | Record<string, any>,
): string {
  const timestamp = new Date().toLocaleString('zh-CN');
  const filename = typeof fileIdOrParams === 'string'
    ? fileIdOrParams
    : (fileIdOrParams?.fileId || 'unknown');

  const findingsHtml = explanation.key_findings.map((f) => `<li>${f}</li>`).join('\n');
  const recsHtml = explanation.recommendations.map((r) => `<li>${r}</li>`).join('\n');
  const overviewHtml = explanation.data_overview
    .map((item) => `<div style="text-align:center;padding:10px 16px;background:rgba(255,255,255,0.15);border-radius:8px;min-width:120px;"><div style="font-size:22px;font-weight:700;">${item.value || ''}</div><div style="font-size:12px;opacity:0.85;margin-top:4px;">${item.metric || ''}</div></div>`)
    .join('\n');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>trendata - ${title}</title>
<script src="https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js"><\/script>
<style>body{margin:0;padding:20px;font-family:'Segoe UI',Arial,sans-serif;background:#f0f2f5}.container{max-width:1400px;margin:0 auto;background:#fff;padding:25px;border-radius:10px;box-shadow:0 4px 20px rgba(0,0,0,.1)}h1{text-align:center;color:#061b31;font-size:28px}.top-stats{display:flex;justify-content:space-around;flex-wrap:wrap;gap:12px;background:linear-gradient(135deg,${COLORS.primary},${COLORS.primaryEnd});padding:20px;border-radius:10px;margin-bottom:25px;color:#fff}.chart-box{background:#fff;padding:15px;border-radius:8px;border:1px solid #e0e0e0;margin-bottom:25px}.chart{width:100%;height:500px}.ai-panel{background:#fafbff;border:1px solid #e8e8f0;border-radius:10px;padding:24px;margin-bottom:25px}.ai-panel h2{color:#061b31;font-size:20px;margin:0 0 16px}.ai-panel h3{color:#061b31;font-size:15px;margin:16px 0 8px;border-bottom:1px solid #eee;padding-bottom:6px}.ai-panel ul{padding-left:20px;margin:0}.ai-panel li{font-size:14px;color:#444;line-height:1.8;margin-bottom:4px}.ai-panel p{font-size:14px;color:#555;line-height:1.8}.footer{text-align:center;padding:20px;font-size:12px;color:#999;border-top:1px solid #eee;margin-top:30px}.fingerprint{font-size:10px;color:#ccc;margin-top:8px}</style></head>
<body><div class="container"><h1>${title}</h1><div class="top-stats">${overviewHtml}</div>
<div class="chart-box"><div id="main-chart" class="chart"></div></div>
<div class="ai-panel"><h2>AI 解释</h2><p>${explanation.summary}</p><h3>核心发现</h3><ul>${findingsHtml}</ul><h3>结论与建议</h3><ul>${recsHtml}</ul></div>
<div class="footer">trendata | 生成时间: ${timestamp} | 数据文件: ${filename}<div class="fingerprint">防伪指纹: ${btoa(`${timestamp}|${filename}|${Date.now()}`)}</div></div></div>
<script>var c=echarts.init(document.getElementById('main-chart'));c.setOption(${JSON.stringify(chartOption)});window.addEventListener('resize',function(){c.resize()});<\/script></body></html>`;
}

// ---- Download HTML ----
export function downloadHtml(html: string, type: string) {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trendata_${type}_${ts}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- Copy 4K image to clipboard (pure function accepting echartsInstance) ----
export async function handleCopy4K(
  echartsInstance: any,
  messageApi: { success: (msg: string) => void; error: (msg: string) => void; info: (msg: string) => void },
) {
  if (!echartsInstance) {
    messageApi.error('图表未就绪');
    return;
  }
  try {
    const dataUrl = echartsInstance.getDataURL({
      pixelRatio: 4,
      backgroundColor: '#fff',
      type: 'png',
    });
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    messageApi.success('已复制 4K 高清图片到剪贴板，可直接粘贴到 PPT/Word');
  } catch {
    messageApi.info('剪贴板不可用，已下载图片');
    const link = document.createElement('a');
    link.href = echartsInstance.getDataURL({ pixelRatio: 4, backgroundColor: '#fff', type: 'png' });
    link.download = `trendata_4k_${Date.now()}.png`;
    link.click();
  }
}

// ---- Safe JSON parse for URL params (K2, K3) ----
export function safeParseParams(rawParams: string): Record<string, any> {
  try {
    return JSON.parse(decodeURIComponent(rawParams));
  } catch {
    return {};
  }
}
